import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { dispatchJobs, dispatchDrivers, dispatchMatch, update } from "@/lib/api";
import { Layout, PageHeader } from "@/components/Layout";
import { DispatchMap } from "@/components/DispatchMap";
import { StatusPill, PriorityPill } from "@/components/StatusPill";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { MapPin, Clock, Truck, Navigation, Gauge, UserCheck, Wand2, Satellite } from "lucide-react";
import { toast } from "sonner";

const hosColor = (s) => (s === "Violation" ? "bg-red-100 text-red-700" : s === "Warning" ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-700");
const Bar = ({ used, limit, color }) => (
  <div className="h-1.5 w-full rounded-full bg-neutral-200 overflow-hidden">
    <div className={`h-full ${color}`} style={{ width: `${Math.min(100, (used / limit) * 100)}%` }} />
  </div>
);

export default function Dispatch() {
  const qc = useQueryClient();
  const { data: jobs = [] } = useQuery({ queryKey: ["dispatch-jobs"], queryFn: dispatchJobs });
  const { data: hos = [] } = useQuery({ queryKey: ["dispatch-drivers"], queryFn: dispatchDrivers });
  const [selectedId, setSelectedId] = useState(null);
  const [match, setMatch] = useState(null);
  const [matching, setMatching] = useState(false);

  const selected = jobs.find((j) => j.id === selectedId);
  const markers = useMemo(() => jobs.map((j) => ({
    id: j.id, lat: j.pickup_lat, lng: j.pickup_lng,
    label: `${j.work_order_no || j.requisition_no} · ${j.project_name || ""}`,
    sub: `${j.pickup_location || "?"} → ${j.delivery_location || "?"}`,
  })), [jobs]);

  const runMatch = async (job) => {
    setSelectedId(job.id);
    setMatch(null);
    setMatching(true);
    try { setMatch(await dispatchMatch({ requisition_id: job.id })); }
    catch (e) { toast.error("Matching failed"); }
    finally { setMatching(false); }
  };

  const assignMut = useMutation({
    mutationFn: ({ job, s }) => update("requisitions", job.id, {
      driver_id: s.driver_id, driver_name: s.driver_name, employee_id: s.employee_id,
      truck_id: s.suggested_truck_id, truck_unit_number: s.suggested_truck_unit, truck_type: s.suggested_truck_type,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["dispatch-jobs"] });
      qc.invalidateQueries({ queryKey: ["dispatch-drivers"] });
      qc.invalidateQueries({ queryKey: ["requisitions"] });
      toast.success("Driver & truck assigned");
    },
    onError: () => toast.error("Assign failed"),
  });

  return (
    <Layout>
      <PageHeader title="Dispatch Board" subtitle="Live job map, driver hours-of-service & route matching">
        <Badge variant="secondary" className="gap-1.5 text-muted-foreground" data-testid="geotab-badge">
          <Satellite className="h-3.5 w-3.5" /> Geotab GPS — planned
        </Badge>
      </PageHeader>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 p-6 animate-fade-up" style={{ height: "calc(100vh - 4rem)" }}>
        {/* LEFT */}
        <div className="flex flex-col min-h-0">
          <Tabs defaultValue="jobs" className="flex flex-col min-h-0 flex-1">
            <TabsList className="w-full">
              <TabsTrigger value="jobs" data-testid="dispatch-tab-jobs" className="flex-1">Jobs ({jobs.length})</TabsTrigger>
              <TabsTrigger value="drivers" data-testid="dispatch-tab-drivers" className="flex-1">Drivers · HOS</TabsTrigger>
            </TabsList>

            <TabsContent value="jobs" className="flex-1 min-h-0 overflow-y-auto space-y-2 mt-3 pr-1">
              {jobs.length === 0 && <div className="text-center py-10 text-muted-foreground text-sm">No active jobs.</div>}
              {jobs.map((j) => (
                <Card key={j.id} data-testid={`dispatch-job-${j.id}`}
                  onClick={() => runMatch(j)}
                  className={`p-3 border-border/60 shadow-none cursor-pointer transition-all ${selectedId === j.id ? "ring-2 ring-[#0055FF] bg-blue-50/40" : "hover:bg-muted/50"}`}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-mono-data text-xs font-semibold text-[#0055FF]">{j.work_order_no || j.requisition_no}</span>
                    <div className="flex gap-1.5"><PriorityPill value={j.priority} /><StatusPill value={j.status} /></div>
                  </div>
                  <div className="text-sm font-medium truncate">{j.project_name || j.load_description || "Untitled"}</div>
                  <div className="mt-1 space-y-0.5 text-xs text-neutral-600">
                    <div className="flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5 text-neutral-400" />{j.pickup_location || "?"} → {j.delivery_location || "?"}</div>
                    <div className="flex items-center gap-1.5"><Truck className="h-3.5 w-3.5 text-neutral-400" />{j.truck_type || "Any"} · {j.estimated_time_hrs || 0}h · {j.driver_name || "Unassigned"}</div>
                  </div>

                  {selectedId === j.id && (
                    <div className="mt-3 pt-3 border-t border-border/60" onClick={(e) => e.stopPropagation()} data-testid="match-panel">
                      <div className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-neutral-700 mb-2">
                        <Wand2 className="h-3.5 w-3.5 text-[#0055FF]" /> Suggested Drivers
                      </div>
                      {matching && <div className="text-xs text-muted-foreground py-2">Matching routes…</div>}
                      {match && match.suggestions.slice(0, 4).map((s) => (
                        <div key={s.driver_id} data-testid={`match-${s.driver_id}`} className="flex items-center justify-between gap-2 py-1.5 border-b border-border/40 last:border-0">
                          <div className="min-w-0">
                            <div className="text-sm font-medium truncate flex items-center gap-1.5">
                              {s.driver_name}
                              {s.feasible ? <UserCheck className="h-3.5 w-3.5 text-emerald-600" /> : <span className="text-[10px] text-red-600">{s.reasons[0]}</span>}
                            </div>
                            <div className="text-[11px] text-muted-foreground font-mono-data">
                              {s.distance_mi != null ? `${s.distance_mi} mi` : "dist n/a"} · {s.remaining_driving_today}h left · {s.suggested_truck_unit ? `truck ${s.suggested_truck_unit}` : "no truck"}
                            </div>
                          </div>
                          <Button size="sm" variant="outline" className="h-7 text-xs shrink-0" data-testid={`assign-${s.driver_id}`}
                            disabled={assignMut.isPending} onClick={() => assignMut.mutate({ job: j, s })}>Assign</Button>
                        </div>
                      ))}
                    </div>
                  )}
                </Card>
              ))}
            </TabsContent>

            <TabsContent value="drivers" className="flex-1 min-h-0 overflow-y-auto space-y-2 mt-3 pr-1">
              {hos.map((d) => (
                <Card key={d.driver_id} data-testid={`hos-${d.driver_id}`} className="p-3 border-border/60 shadow-none">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-sm font-semibold">{d.driver_name} <span className="text-xs text-muted-foreground font-normal">· {d.home_base || "—"}</span></div>
                    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold ${hosColor(d.hos_status)}`}>{d.hos_status}</span>
                  </div>
                  <div className="space-y-2">
                    <div>
                      <div className="flex justify-between text-[11px] text-neutral-600 mb-0.5"><span className="flex items-center gap-1"><Gauge className="h-3 w-3" />Daily driving</span><span className="font-mono-data">{d.assigned_today_hours}/{d.drive_limit}h · {d.remaining_driving_today}h left</span></div>
                      <Bar used={d.assigned_today_hours} limit={d.drive_limit} color={d.assigned_today_hours > d.drive_limit ? "bg-red-500" : "bg-[#0055FF]"} />
                    </div>
                    <div>
                      <div className="flex justify-between text-[11px] text-neutral-600 mb-0.5"><span>8-day cycle</span><span className="font-mono-data">{d.assigned_cycle_hours}/{d.cycle_limit}h</span></div>
                      <Bar used={d.assigned_cycle_hours} limit={d.cycle_limit} color="bg-neutral-800" />
                    </div>
                    <div className="text-[11px] text-neutral-600 flex items-center gap-1.5">
                      <Navigation className="h-3 w-3 text-neutral-400" /> Vehicles: {d.vehicles_assigned.length ? d.vehicles_assigned.join(", ") : "none"} · {d.job_count} active job{d.job_count === 1 ? "" : "s"}
                    </div>
                  </div>
                </Card>
              ))}
            </TabsContent>
          </Tabs>
        </div>

        {/* RIGHT: MAP */}
        <div className="min-h-0 h-full">
          <DispatchMap markers={markers} selectedId={selectedId} onSelect={(id) => { const j = jobs.find((x) => x.id === id); if (j) runMatch(j); }} />
        </div>
      </div>
    </Layout>
  );
}
